import React, { useState } from 'react';
import { FileText, Plus, Trash2, ExternalLink, AlertTriangle } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import AgreementUploadModal from './AgreementUploadModal';

import { RubyGemMark } from '../RubyGemMark';
interface Agreement {
  id: string;
  agreementName?: string;
  maxRate?: number;
  maxFlat?: number;
  fileName: string;
  uploadedAt: number;
  uploadedBy?: string;
  sizeBytes?: number;
  effectiveDate?: string;
  version?: string;
  status: 'active' | 'archived';
}

interface Props {
  tenantId: string;
  locationId: string;
  agreements: Agreement[];
  activeAgreementId: string | null;
  onChanged: () => void;
  /** When true, the list renders without its top border/padding (used inside its own card). */
  standalone?: boolean;
}

const SurchargeAgreementsList: React.FC<Props> = ({
  tenantId,
  locationId,
  agreements,
  activeAgreementId,
  onChanged,
  standalone = false,
}) => {
  const [showUpload, setShowUpload] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Agreement | null>(null);

  const sorted = [...agreements].sort((a, b) => b.uploadedAt - a.uploadedAt);

  const handleSetActive = async (id: string) => {
    try {
      setBusyId(id);
      await api.posNmi.setActiveAgreement(tenantId, locationId, id);
      toastService.success('Active agreement updated');
      onChanged();
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to set active');
    } finally {
      setBusyId(null);
    }
  };

  const handleView = async (id: string) => {
    try {
      setBusyId(id);
      const { downloadUrl } = await api.posNmi.getAgreementDownloadUrl(tenantId, locationId, id);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to open');
    } finally {
      setBusyId(null);
    }
  };

  const requestDelete = (a: Agreement) => {
    if (a.id === activeAgreementId) {
      toastService.error('Set another agreement active first');
      return;
    }
    setConfirmDelete(a);
  };

  const handleDelete = async (a: Agreement) => {
    try {
      setBusyId(a.id);
      setConfirmDelete(null);
      await api.posNmi.deleteAgreement(tenantId, locationId, a.id);
      toastService.success(`Deleted ${a.fileName}`);
      onChanged();
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className={standalone ? '' : 'border-t border-stone-200 pt-4'}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-stone-700">Agreements ({agreements.length})</p>
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 inline-flex items-center gap-1.5"
          >
            <Plus size={12} />
            Create Agreement
          </button>
        </div>

        {sorted.length === 0 ? (
          <div className="p-4 rounded-lg border border-dashed border-stone-300 bg-stone-50 text-center">
            <FileText size={20} className="mx-auto text-stone-400 mb-1" />
            <p className="text-xs text-stone-500">No agreements uploaded yet</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {sorted.map((a) => {
              const isActive = a.id === activeAgreementId;
              return (
                <div
                  key={a.id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border ${
                    isActive ? 'border-emerald-200 bg-emerald-50/50' : 'border-stone-200 bg-stone-50/50'
                  }`}
                >
                  <FileText size={16} className={isActive ? 'text-emerald-600' : 'text-stone-400'} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium truncate ${isActive ? 'text-stone-800' : 'text-stone-600'}`}>
                      {a.agreementName || a.fileName}
                    </p>
                    <p className="text-[10px] text-stone-500 flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-stone-700">
                        {Number(a.maxRate || 0).toFixed(2)}% + ${Number(a.maxFlat || 0).toFixed(2)}
                      </span>
                      <span>·</span>
                      <span className="truncate">{a.fileName}</span>
                      {a.version && <><span>·</span><span>{a.version}</span></>}
                      <span>·</span>
                      <span>{new Date(a.uploadedAt).toLocaleDateString()}</span>
                      {a.sizeBytes ? <><span>·</span><span>{Math.round(a.sizeBytes / 1024)} KB</span></> : null}
                    </p>
                  </div>
                  {/*
                    ONE SWITCH PER AGREEMENT, and exactly one may be on.

                    A switch rather than a "Set Active" button because that is
                    the question being asked of each row — is THIS the one in
                    force — and a row showing a static ACTIVE badge while its
                    neighbours showed buttons made the set read like six
                    unrelated controls instead of one choice.

                    The active row's switch is deliberately INERT: turning the
                    only agreement off would leave a surcharging location with
                    no ceiling and no document behind it. Switching a
                    different one on is how the active agreement changes, and
                    the service archives the previous one in the same write.
                  */}
                  {isActive
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white font-medium">ACTIVE</span>
                    : null}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isActive}
                    aria-label={isActive ? `${a.agreementName || a.fileName} is the active agreement` : `Make ${a.agreementName || a.fileName} the active agreement`}
                    title={isActive
                      ? 'This is the active agreement. Switch another on to replace it.'
                      : 'Make this the active agreement — the current one is archived.'}
                    disabled={isActive || busyId === a.id}
                    onClick={() => handleSetActive(a.id)}
                    className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
                      isActive ? 'bg-emerald-500 cursor-default' : 'bg-stone-300 hover:bg-stone-400'
                    } disabled:opacity-100`}
                  >
                    <span className={`mt-0.5 inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      isActive ? 'translate-x-[18px]' : 'translate-x-[2px]'
                    }`} />
                  </button>
                  <button
                    onClick={() => handleView(a.id)}
                    disabled={busyId === a.id}
                    className="text-stone-500 hover:text-stone-700 p-1"
                    title="View PDF"
                  >
                    {busyId === a.id ? <RubyGemMark size={12} drawing color="currentColor" /> : <ExternalLink size={12} />}
                  </button>
                  {!isActive && (
                    <button
                      onClick={() => requestDelete(a)}
                      disabled={busyId === a.id}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showUpload && (
        <AgreementUploadModal
          tenantId={tenantId}
          locationId={locationId}
          onClose={() => setShowUpload(false)}
          onUploaded={onChanged}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true">
          <div className="bg-stone-900 text-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-stone-700">
            <div className="flex items-start gap-3 p-5 border-b border-stone-800">
              <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} className="text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base">Delete Agreement?</h3>
                <p className="text-stone-400 text-xs mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-stone-200">
                Continue? This will <span className="font-semibold text-red-300">permanently delete</span> the agreement PDF and its surcharge ceilings.
              </p>
              <div className="rounded-lg bg-stone-800 border border-stone-700 p-3 space-y-1">
                <p className="text-xs text-stone-400">Agreement</p>
                <p className="text-sm font-semibold text-white truncate">{confirmDelete.agreementName || confirmDelete.fileName}</p>
                <p className="text-[11px] font-mono text-stone-400 truncate">
                  {Number(confirmDelete.maxRate || 0).toFixed(2)}% + ${Number(confirmDelete.maxFlat || 0).toFixed(2)}
                  {confirmDelete.version ? ` · ${confirmDelete.version}` : ''}
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-stone-800 bg-stone-950 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm font-medium text-stone-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                <Trash2 size={14} />
                Delete Agreement
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SurchargeAgreementsList;
